// Fork from legacy-packages/with-toast/src/hoc.tsx

import React from 'react';

// Components
import Portal from '@nebula/portal';
import { CSSTransition } from 'react-transition-group';
import Toast from './Toast';

// Types
import type { WithToastProps, ToastOption } from './types';

import styles from './style.scss';

const DEFAULT_TOAST_DURATION = 2000; // ms
const TRANSITION_DURATION = 200; //ms

function withToast<T extends WithToastProps>(
  WrappedComponent: React.ComponentType<T>
): React.ComponentType<Omit<T, keyof WithToastProps>> {
  const ComponentWithToast = (
    props: Omit<T, keyof WithToastProps>
  ): JSX.Element => {
    const [toastInfo, setToastInfo] = React.useState<
      ToastOption & { isVisible: boolean }
    >({
      text: '',
      level: 'success',
      isVisible: false,
    });
    const timeout = React.useRef<number>();

    const closeToast = () => {
      setToastInfo(toastInfo => ({ ...toastInfo, isVisible: false }));
    };

    const showToast = (option: ToastOption) => {
      if (toastInfo.isVisible) {
        closeToast();
      }
      setToastInfo({ ...option, isVisible: true });
    };

    const onEntered = () => {
      const duration = toastInfo.duration || DEFAULT_TOAST_DURATION;
      timeout.current = window.setTimeout(closeToast, duration);
    };

    const onExit = () => {
      window.clearTimeout(timeout.current);
    };

    return (
      <>
        <Portal>
          <CSSTransition
            in={toastInfo.isVisible}
            timeout={TRANSITION_DURATION}
            onEntered={onEntered}
            onExit={onExit}
            onExited={toastInfo.onDismiss}
            mountOnEnter
            unmountOnExit
            classNames={{
              enter: styles.enter,
              enterActive: styles.enterActive,
              exit: styles.exit,
              exitActive: styles.exitActive,
            }}
          >
            <div className={styles.container} onClick={closeToast}>
              <Toast text={toastInfo.text} level={toastInfo.level} />
            </div>
          </CSSTransition>
        </Portal>
        <WrappedComponent {...(props as T)} showToast={showToast} />
      </>
    );
  };

  return ComponentWithToast;
}

export default withToast;
